(() => {
  const priceInputSelector = '[data-filter-form] input[type="number"]';

  const revealCards = (root) => {
    const cards = root.querySelectorAll('[data-collection-card]');
    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      }), { threshold: 0.08, rootMargin: '0px 0px -30px' });
      cards.forEach((card, index) => {
        card.style.setProperty('--card-delay', `${(index % 4) * 55}ms`);
        observer.observe(card);
      });
    } else {
      cards.forEach((card) => card.classList.add('is-visible'));
    }
  };

  const showSkeleton = (root) => {
    const results = root.querySelector('.collection-redesign__results');
    if (!results || results.querySelector('.collection-redesign__skeleton')) return;
    const skeleton = document.createElement('div');
    skeleton.className = 'collection-redesign__skeleton';
    skeleton.setAttribute('aria-hidden', 'true');
    skeleton.innerHTML = Array.from({ length: 8 }, () => '<div class="collection-redesign__skeleton-card"><span></span><i></i><i></i><i></i><b></b></div>').join('');
    results.append(skeleton);
  };

  const initialise = (root) => {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    let requestController;
    let priceTimer;
    let activeURL = window.location.href;
    const stockSorter = root.hasAttribute('data-default-sort') ? window.himpStockSort : null;

    const close = () => {
      root.classList.remove('filters-open');
      root.querySelector('[data-filter-open]')?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    const open = () => {
      root.classList.add('filters-open');
      root.querySelector('[data-filter-open]')?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      root.querySelector('[data-filter-panel] input, [data-filter-panel] button, [data-filter-panel] a')?.focus();
    };

    const load = async (url, addHistory = true) => {
      const dynamic = root.querySelector('[data-collection-dynamic]');
      if (!dynamic) return;
      requestController?.abort();
      const controller = new AbortController();
      requestController = controller;
      dynamic.setAttribute('aria-busy', 'true');
      root.classList.add('is-loading');
      showSkeleton(root);

      try {
        const response = await fetch(url, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incomingRoot = page.querySelector('[data-collection-page]');
        if (incomingRoot && stockSorter) await stockSorter.prepare(incomingRoot, url, controller.signal);
        controller.signal.throwIfAborted();
        const nextDynamic = page.querySelector('[data-collection-page] [data-collection-dynamic]');
        if (!nextDynamic) throw new Error('Collection response is missing its dynamic content');
        dynamic.replaceWith(nextDynamic);
        activeURL = new URL(url, window.location.href).href;
        if (addHistory) window.history.pushState({}, '', url);
        if (page.title) document.title = page.title;
        if (root.classList.contains('filters-open')) {
          root.querySelector('[data-filter-open]')?.setAttribute('aria-expanded', 'true');
        }
        revealCards(root);
      } catch (error) {
        if (error.name !== 'AbortError') {
          const status = root.querySelector('[data-collection-scroll-status]');
          if (status) status.textContent = 'Could not update products. Please try again.';
          else window.location.assign(url);
        }
      } finally {
        if (requestController === controller) {
          root.querySelector('[data-collection-dynamic]')?.removeAttribute('aria-busy');
          root.classList.remove('is-loading');
          root.querySelector('.collection-redesign__skeleton')?.remove();
        }
      }
    };

    const filterURL = (form) => {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      if (stockSorter?.enabled(new URL(window.location.href))) { params.delete('sort_by'); params.set('himp_sort', 'availability'); }
      const current = new URL(window.location.href);
      for (const key of ['preview_theme_id', 'view']) if (current.searchParams.has(key)) params.set(key, current.searchParams.get(key));
      const query = params.toString();
      return query ? `${form.action}?${query}` : form.action;
    };

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-open]')) open();
      if (event.target.closest('[data-filter-close]')) close();
      const link = event.target.closest('.collection-redesign__filter-heading a, .collection-redesign__pagination a, .collection-redesign__no-results a');
      if (link && link.origin === window.location.origin) {
        event.preventDefault();
        const target = new URL(link.href);
        if (stockSorter?.enabled(new URL(location.href))) { target.searchParams.delete('sort_by'); target.searchParams.set('himp_sort', 'availability'); }
        load(target.href);
      }
    });

    root.addEventListener('change', (event) => {
      const filterForm = event.target.closest('[data-filter-form]');
      if (filterForm) {
        window.clearTimeout(priceTimer);
        load(filterURL(filterForm));
        return;
      }
      if (event.target.matches('[data-sort-select]')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('himp_sort');
        url.searchParams.delete('himp_page');
        if (event.target.value === 'himp-stock') { url.searchParams.delete('sort_by'); url.searchParams.set('himp_sort', 'availability'); }
        else url.searchParams.set('sort_by', event.target.value);
        url.searchParams.delete('page');
        load(url.href);
      }
    });

    root.addEventListener('input', (event) => {
      if (!event.target.matches(priceInputSelector)) return;
      window.clearTimeout(priceTimer);
      priceTimer = window.setTimeout(() => load(filterURL(event.target.form)), 600);
    });

    root.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-filter-form], .collection-redesign__sort');
      if (!form) return;
      event.preventDefault();
      if (form.matches('[data-filter-form]')) load(filterURL(form));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.classList.contains('filters-open')) close();
    });
    window.addEventListener('popstate', () => {
      if (window.location.href !== activeURL) load(window.location.href, false);
    });
    if (!stockSorter) { revealCards(root); return; }
    const initial = new AbortController();
    requestController = initial;
    root.classList.add('is-loading');
    showSkeleton(root);
    Promise.resolve().then(() => stockSorter.prepare(root, location.href, initial.signal)).catch(error => {
      if (error.name === 'AbortError') return;
      const more = root.querySelector('[data-collection-more]');
      if (more) { const fallback = new URL(more.href); fallback.searchParams.delete('himp_sort'); fallback.searchParams.set('sort_by', root.dataset.defaultSort); more.href = fallback.href; }
      const select = root.querySelector('[data-sort-select]');
      if (select) select.value = new URL(location.href).searchParams.get('sort_by') || root.dataset.defaultSort;
      const status = root.querySelector('[data-collection-scroll-status]');
      if (status) status.textContent = 'In-stock sorting could not load. Showing the standard order; choose In stock first to retry.';
    }).finally(() => {
      if (requestController !== initial) return;
      root.querySelector('.collection-redesign__skeleton')?.remove();
      root.classList.remove('is-loading');
      revealCards(root);
    });
  };

  const initialiseAll = (scope = document) => scope.querySelectorAll('[data-collection-page]').forEach(initialise);
  document.addEventListener('DOMContentLoaded', () => initialiseAll());
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
